import { useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  FormGroup,
  FormLabel,
  TextField,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import { columnTextField } from "utils/tables-utils";
import ImagePlaceholder from "components/Image/ImagePlaceholder";
import componentButtonStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import MaskAndValid from "utils/maskAndValid";
import { IS_PROD_ENV } from "constants/environment";

const useComponentStyles = makeStyles(componentButtonStyles);

const DEFAULT_VALUE = "-";

const BASE_URL = IS_PROD_ENV ? "karsten.digital" : "hml.karsten.digital";

const createWhatsAppLink = (phone, token, isPrivate, cpf) => {
  if (!phone || !token) return null;
  let redirectPage = `https://${BASE_URL}/vitrine-virtual-cliente?hashVitrine=${token}`;
  if (isPrivate && cpf) {
    const cpfDigits = cpf.replace(/[^\d]+/g, "");
    redirectPage += `&cpf=${cpfDigits}`;
  }
  return isPrivate
    ? `https://wa.me/55${phone}?text=Visite a vitrine virtual criada para você! ${encodeURIComponent(
        redirectPage
      )}`
    : redirectPage;
};

const formatPrice = (row) => {
  if (row?.Preco === row?.PrecoPromo || row?.PrecoPromo === 0) {
    return row?.PrecoFormat;
  }
  return row?.PrecoPromoFormat;
};

export default function DialogDetailShowcase({ data, type, handleClose }) {
  const classes = { ...useComponentStyles() };

  const [showcaseDetail, setShowcaseDetail] = useState(null);
  const [phone, setPhone] = useState(DEFAULT_VALUE);
  const [link, setLink] = useState(null);

  const modelTableItemDetail = [
    {
      dataField: "Foto",
      text: "Produto",
      align: "center",
      headerAlign: "center",
      isDummyField: true,
      formatter: (cell, row) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ImagePlaceholder
            src={row.Foto}
            alt="Foto do produto"
            width={50}
            widthOnError={40}
            imagePreviewTitle=""
          />
        </div>
      ),
      csvExport: true,
    },
    columnTextField(
      {
        dataField: "DescricaoProduto",
        text: "Descrição",
        align: "left",
        formatter: (cell) => <p style={{ whiteSpace: "normal" }}>{cell}</p>,
      },
      false
    ),
  ];

  const modelTableItemDetailHistoric = [
    {
      dataField: "Foto",
      text: "",
      align: "center",
      headerAlign: "center",
      isDummyField: true,
      headerStyle: { width: 75 },
      formatter: (cell, row) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ImagePlaceholder
            src={row.Foto}
            alt="Foto do produto"
            width={75}
            widthOnError={60}
            imagePreviewTitle=""
          />
        </div>
      ),
      csvExport: true,
    },
    columnTextField(
      {
        dataField: "IdProduto",
        text: "IdProduto",
        align: "center",
      },
      false
    ),
    columnTextField(
      {
        dataField: "DescricaoProduto",
        text: "Descrição",
        headerStyle: { width: 250 },
        align: "left",
        formatter: (cell) => (
          <p style={{ whiteSpace: "normal", fontSize: "0.8rem" }}>{cell}</p>
        ),
      },
      false
    ),
    columnTextField(
      {
        dataField: "Preco",
        text: "Preço",
        align: "center",
        formatter: (cell, row) => (
          <p style={{ fontSize: "0.8rem" }}>R$ {formatPrice(row)}</p>
        ),
      },
      false
    ),
    columnTextField(
      {
        dataField: "Quantidade",
        text: "Quantidade",
        align: "center",
      },
      false
    ),
  ];

  useEffect(() => {
    if (showcaseDetail) {
      setPhone(
        showcaseDetail?.Telefone
          ? MaskAndValid.maskPhone(showcaseDetail.Telefone)
          : DEFAULT_VALUE
      );
      setLink(
        createWhatsAppLink(
          showcaseDetail?.Telefone,
          showcaseDetail?.TokenVitrineConsumidor,
          data?.VitrinePublica === false,
          data?.CpfConsumidor
        )
      );
    }
  }, [showcaseDetail]);

  useEffect(() => {
    if (data) {
      setShowcaseDetail(data);
    }
  }, [type, data]);

  const renderProductsTable = () => {
    if (type === "historic") {
      if (
        showcaseDetail?.StatusVitrineConsumidor !== "ABERTO" &&
        showcaseDetail?.ItensConsumidor?.length !== 0
      ) {
        return (
          <ToolkitProvider
            data={showcaseDetail?.ItensConsumidor}
            keyField="idProduto"
            columns={modelTableItemDetailHistoric}
          >
            {(props) => (
              <PerfectScrollbar>
                <BootstrapTable
                  {...props.baseProps}
                  bootstrap4
                  bordered={false}
                  condensed
                  striped
                  hover
                  style={{ overflow: "auto!important" }}
                />
              </PerfectScrollbar>
            )}
          </ToolkitProvider>
        );
      }
      return (
        <TextField
          name="products"
          required
          fullWidth
          disabled
          value="Nenhum produto inserido no carrinho."
          variant="outlined"
        />
      );
    }

    return (
      <ToolkitProvider
        data={data?.Itens}
        keyField="DescricaoProduto"
        columns={modelTableItemDetail}
      >
        {(props) => (
          <PerfectScrollbar>
            <BootstrapTable
              {...props.baseProps}
              bootstrap4
              bordered={false}
              condensed
              striped
              hover
              style={{ overflow: "auto!important" }}
            />
          </PerfectScrollbar>
        )}
      </ToolkitProvider>
    );
  };

  const renderWhatsAppLink = () => {
    if (
      type !== "historic" ||
      showcaseDetail?.TokenExpirado ||
      showcaseDetail?.TokenExpirado === "true" ||
      !["ABERTO", "PENDENTE"].includes(showcaseDetail?.StatusVitrineConsumidor)
    ) {
      return null;
    }

    const isPrivate = data?.VitrinePublica === false;

    if (isPrivate) {
      return (
        <Grid item xs={12}>
          <a
            href={link}
            target="_blank"
            style={{ color: "inherit", cursor: "pointer" }}
            rel="noreferrer"
          >
            <FormGroup>
              <FormLabel style={{ cursor: "pointer" }}>
                Clique para enviar o link criado
                <Box
                  component={WhatsAppIcon}
                  position="relative"
                  color="green"
                  marginLeft="0.5rem"
                  cursor="pointer"
                  width="1.25rem!important"
                  height="1.25rem!important"
                />
              </FormLabel>
            </FormGroup>
          </a>
        </Grid>
      );
    } else {
      return (
        <Grid item xs={12}>
          <FormGroup>
            <FormLabel style={{ cursor: "pointer" }}>
              Copiar link para compartilhamento
              <Box
                component={FileCopyIcon}
                position="relative"
                color="primary"
                marginLeft="0.5rem"
                cursor="pointer"
                width="1.25rem!important"
                height="1.25rem!important"
                onClick={() => {
                  navigator.clipboard.writeText(link);
                }}
              />
            </FormLabel>
          </FormGroup>
        </Grid>
      );
    }
  };

  const renderStatusMessage = () => {
    if (
      showcaseDetail?.StatusVitrineConsumidor === "CONFIRMADO" &&
      (!showcaseDetail?.TokenExpirado ||
        showcaseDetail?.TokenExpirado === "false")
    ) {
      return (
        <Typography variant="h4" component="h4" style={{ color: "green" }}>
          Vitrine Confirmada! Consulte o app para finalizar a venda.
        </Typography>
      );
    }

    if (
      showcaseDetail?.TokenExpirado ||
      showcaseDetail?.TokenExpirado === "true"
    ) {
      return (
        <Typography
          variant="h4"
          component="h4"
          style={{ color: "grey", textAlign: "center" }}
        >
          Link da Vitrine Virtual expirado. Crie novamente para enviar ao
          cliente.
        </Typography>
      );
    }

    return null;
  };

  return (
    <Dialog
      maxWidth="lg"
      open
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle>
        <Typography variant="h3" component="h3">
          Detalhes da Vitrine Virtual
        </Typography>
      </DialogTitle>

      <PerfectScrollbar>
        <DialogContent style={{ padding: "2rem" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormGroup>
                <FormLabel>Nome Vitrine</FormLabel>
                <TextField
                  name="nome"
                  required
                  fullWidth
                  disabled
                  value={data?.Nome || showcaseDetail?.Nome || DEFAULT_VALUE}
                  variant="outlined"
                />
              </FormGroup>
            </Grid>

            {type === "historic" ? (
              <>
                <Grid item xs={12}>
                  <FormGroup>
                    <FormLabel>Consumidor</FormLabel>
                    <TextField
                      name="usuario"
                      required
                      fullWidth
                      disabled
                      value={showcaseDetail?.NomeConsumidor || DEFAULT_VALUE}
                      variant="outlined"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={12}>
                  <FormGroup>
                    <FormLabel>Telefone</FormLabel>
                    <TextField
                      name="telefone"
                      required
                      fullWidth
                      disabled
                      value={phone}
                      variant="outlined"
                    />
                  </FormGroup>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <FormGroup>
                  <FormLabel>Usuário</FormLabel>
                  <TextField
                    name="usuario"
                    required
                    fullWidth
                    disabled
                    value={data?.NomeUsuario || DEFAULT_VALUE}
                    variant="outlined"
                  />
                </FormGroup>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormGroup>
                <FormLabel>Loja</FormLabel>
                <TextField
                  name="loja"
                  required
                  fullWidth
                  disabled
                  value={data?.Loja || showcaseDetail?.Loja || DEFAULT_VALUE}
                  variant="outlined"
                />
              </FormGroup>
            </Grid>

            {type === "historic" ? (
              <Grid item xs={12}>
                <FormGroup>
                  <FormLabel>Vendedor</FormLabel>
                  <TextField
                    name="vendedor"
                    required
                    fullWidth
                    disabled
                    value={showcaseDetail?.Vendedor || DEFAULT_VALUE}
                    variant="outlined"
                  />
                </FormGroup>
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <FormGroup>
                    <FormLabel>Data Vigência</FormLabel>
                  </FormGroup>
                  <Grid
                    container
                    style={{ display: "flex", marginTop: "-1rem" }}
                  >
                    <Grid item xs={12} sm={6}>
                      <FormGroup>
                        <FormLabel>Início</FormLabel>
                        <TextField
                          name="inicio"
                          required
                          fullWidth
                          disabled
                          value={
                            data?.DataVigenciaInicioFormat || DEFAULT_VALUE
                          }
                          variant="outlined"
                        />
                      </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormGroup>
                        <FormLabel>Fim</FormLabel>
                        <TextField
                          name="fim"
                          required
                          fullWidth
                          disabled
                          value={data?.DataVigenciaFimFormat || DEFAULT_VALUE}
                          variant="outlined"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <FormGroup>
                    <FormLabel>Status</FormLabel>
                    <FormControlLabel
                      style={{ marginLeft: "-0.5rem" }}
                      control={
                        <Switch checked={data?.Ativo} disabled size="small" />
                      }
                      label={data?.Ativo ? "Ativa" : "Inativa"}
                      labelPlacement="end"
                      value="end"
                      classes={{
                        root: classes.formControlLabelRoot,
                        label: classes.formControlLabelLabel,
                      }}
                    />
                  </FormGroup>
                </Grid>
              </>
            )}

            {type === "historic" && (
              <Grid item xs={12}>
                <FormGroup>
                  <FormLabel>Status Vitrine</FormLabel>
                  <TextField
                    name="status"
                    required
                    fullWidth
                    disabled
                    value={
                      showcaseDetail?.StatusVitrineConsumidor || DEFAULT_VALUE
                    }
                    variant="outlined"
                  />
                </FormGroup>
              </Grid>
            )}

            {renderWhatsAppLink()}

            <Grid item xs={12}>
              {renderProductsTable()}
            </Grid>
          </Grid>
        </DialogContent>
      </PerfectScrollbar>

      <DialogActions>
        {renderStatusMessage()}
        <Button component={Box} onClick={handleClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
